import React from "react";
import { Box, Button, CircularProgress, Stack, Typography, useTheme } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import CelebrationIcon from "@mui/icons-material/Celebration";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import { ReportJobStatus } from "../../../shared/interfaces/JobStatus";
import { Job } from "../../../store/jobStore";
import { progressGradientAnimation } from "../animations";
import { ProgressBar } from "./ProgressBar";
import {
  getAwaitingInputAccentColor,
  getAwaitingInputOutlinedButtonSx,
} from "../../../utils/awaitingInputStyles";
import {
  getAwaitingInputActionLabel,
  getAwaitingInputDescription,
} from "../../../features/hitl/presentPlan";
import { useHitlEnabled } from "../../../hooks/useHitlEnabled";

interface Props {
  job: Job;
  onRetry: (job: Job, event: React.MouseEvent) => void;
  onOpen?: (event: React.MouseEvent) => void;
  retryingJob: boolean;
}

export const JobStatusContent: React.FC<Props> = ({
  job,
  onRetry,
  onOpen,
  retryingJob,
}) => {
  const theme = useTheme();
  const hitlEnabled = useHitlEnabled();
  const awaitingInputAccent = getAwaitingInputAccentColor(theme);

  switch (job.status) {
    case ReportJobStatus.PENDING:
      return (
        <Box>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Box sx={{ position: "relative", display: "inline-flex" }}>
              <CircularProgress
                variant="determinate"
                value={job.progress}
                size={56}
                thickness={4}
                sx={{
                  color: "primary.main",
                  "& .MuiCircularProgress-circle": { strokeLinecap: "round" },
                }}
              />
              <Box
                sx={{
                  top: 0,
                  left: 0,
                  bottom: 0,
                  right: 0,
                  position: "absolute",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography variant="caption" fontWeight="bold" color="primary">
                  {job.progress}%
                </Typography>
              </Box>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontSize: "1rem", fontWeight: 600, color: "text.primary", lineHeight: 1.4 }}>
                Creating report...
              </Typography>
              {job.progressMessage && (
                <Typography
                  sx={{
                    display: "block",
                    fontSize: "0.875rem",
                    fontWeight: 400,
                    mt: 0.25,
                    background: (theme) => theme.palette.mode === 'dark' 
                      ? "linear-gradient(90deg, #90caf9, #b3e5fc, #90caf9)"
                      : "linear-gradient(90deg, #26293B, #4a4e69, #26293B)",
                    backgroundSize: "200% 100%",
                    animation: `${progressGradientAnimation} 2s ease infinite`,
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                  }}
                >
                  {job.progressMessage}
                </Typography>
              )}
            </Box>
          </Stack>

          <ProgressBar progress={job.progress} />
        </Box>
      );

    case ReportJobStatus.PENDING_REVIEW:
      return (
        <Stack direction="row" alignItems="center" spacing={1}>
          <CelebrationIcon sx={{ color: "success.main", fontSize: 22 }} />
          <Typography sx={{ fontSize: "1rem", fontWeight: 600, color: "success.main" }}>
            Report is ready! Click to view.
          </Typography>
        </Stack>
      );

    case ReportJobStatus.AWAITING_INPUT:
      return (
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <PendingActionsIcon sx={{ color: awaitingInputAccent, fontSize: 22 }} />
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: "1rem", fontWeight: 600, color: awaitingInputAccent }}>
              AI Assistant needs clarification.
            </Typography>
            <Typography sx={{ fontSize: "0.875rem", color: "text.secondary" }}>
              {getAwaitingInputDescription(hitlEnabled)}
            </Typography>
          </Box>
          {onOpen && (
            <Button
              variant="outlined"
              size="small"
              onClick={(event) => onOpen(event)}
              sx={getAwaitingInputOutlinedButtonSx(theme)}
            >
              {getAwaitingInputActionLabel(hitlEnabled)}
            </Button>
          )}
        </Stack>
      );

    case ReportJobStatus.FAILED:
      return (
        <Box>
          {job.error && (
            <Typography
              sx={{
                display: "block",
                fontSize: "0.875rem",
                color: "text.secondary",
                mb: 1.5,
                lineHeight: 1.4,
              }}
            >
              {job.error}
            </Typography>
          )}
          <Button
            startIcon={<RefreshIcon />}
            variant="contained"
            color="primary"
            size="small"
            onClick={(e) => onRetry(job, e)}
            fullWidth
            disabled={retryingJob}
            sx={{
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: 600,
              transition: "all 0.2s ease",
              "&:hover": { transform: "scale(1.02)" },
              "&:active": { transform: "scale(0.98)" },
            }}
          >
            {retryingJob ? "Retrying..." : "Try Again"}
          </Button>
        </Box>
      );

    default:
      return (
        <Typography variant="body2" color="text.secondary">
          Unknown status: {job.status}
        </Typography>
      );
  }
};
